/**
 * ChatPanel DOM Renderer
 *
 * Handles all DOM creation and manipulation for the chat panel.
 * Separates rendering logic from business logic.
 *
 * This module extracts DOM rendering from panel.ts to reduce its size
 * and improve maintainability while preserving all functionality.
 */

import type { WidgetConfig, ChatMessage } from '../../core/config';
import { filterSampleQuestions } from '../../core/config';
import { t } from '../../core/i18n';
import {
  renderMessage,
  createTypingIndicator,
  createWelcomeMessage,
  createErrorMessage,
  createUsageLimitMessage,
  type MessageRenderOptions
} from '../message';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Panel display style determines layout and behavior
 */
export type PanelStyle = 'bubble' | 'immersive' | 'inline' | 'vertical';

/**
 * DOM element references for the panel
 */
export interface PanelElements {
  panel: HTMLElement;
  messagesContainer: HTMLElement | null;
  inputElement: HTMLInputElement | null;
  sendButton: HTMLButtonElement | null;
  speedToggleBtn: HTMLButtonElement | null;
}

// =============================================================================
// CHAT PANEL RENDERER CLASS
// =============================================================================

/**
 * ChatPanel DOM renderer
 *
 * Responsible for creating and updating DOM elements.
 * Produces HTML that exactly matches the original panel.ts implementation.
 */
export class ChatPanelRenderer {
  private readonly config: WidgetConfig;
  private readonly style: PanelStyle;

  constructor(config: WidgetConfig, style: PanelStyle) {
    this.config = config;
    this.style = style;
  }

  // ===========================================================================
  // PANEL CREATION
  // ===========================================================================

  /**
   * Build the panel class name based on style and position
   */
  buildPanelClassName(): string {
    const position = this.config.position ?? 'bottom-right';

    switch (this.style) {
      case 'immersive':
        return 'pms-panel pms-panel-immersive';
      case 'vertical':
        return 'pms-panel pms-panel-vertical';
      case 'inline':
        return 'pms-panel pms-panel-inline';
      default:
        return `pms-panel pms-panel-${position}`;
    }
  }

  /**
   * Apply theme CSS custom properties to an element
   */
  applyThemeProperties(element: HTMLElement): void {
    const theme = this.config.theme ?? {};
    const setProperty = (name: string, value?: string) => {
      if (value) element.style.setProperty(name, value);
    };

    setProperty('--pms-primary', theme.primaryColor);
    setProperty('--pms-font', theme.fontFamily);
    setProperty('--pms-border-radius', theme.borderRadius);
    setProperty('--pms-panel-width', theme.panelWidth);
    setProperty('--pms-panel-height', theme.panelHeight);
  }

  /**
   * Build the complete panel HTML structure
   */
  buildPanelHTML(speedModeEnabled: boolean): string {
    // Always include ambient — hidden naturally in small panels, needed for style switching
    const ambientHtml = this.buildAmbientHTML();
    const headerHtml = this.buildHeaderHTML();
    const inputHtml = this.buildInputHTML(speedModeEnabled);

    return `
      ${ambientHtml}
      ${headerHtml}
      <div class="pms-panel-messages"></div>
      ${inputHtml}
    `;
  }

  /**
   * Build ambient background HTML (for immersive style only)
   */
  private buildAmbientHTML(): string {
    return `
      <div class="pms-ambient">
        <div class="pms-ambient__mesh"></div>
        <div class="pms-ambient__vignette"></div>
      </div>
    `;
  }

  /**
   * Build header HTML with appropriate controls for panel style
   */
  private buildHeaderHTML(): string {
    const branding = this.config.branding;
    const logoHtml = branding?.logoUrl
      ? `<img src="${branding.logoUrl}" alt="${branding.logoAlt || branding.companyName || 'Logo'}" class="pms-panel-logo" />`
      : '';

    // Header always rendered (CSS hides it for immersive via .pms-panel-immersive .pms-panel-header { display: none })
    // This ensures it's available when switching from immersive → bubble at runtime
    return `
      <div class="pms-panel-header">
        ${logoHtml}
        <button class="pms-panel-expand" aria-label="Expand to sidebar" title="Expand to sidebar" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <line x1="15" y1="3" x2="15" y2="21"/>
          </svg>
        </button>
        <button class="pms-panel-close" aria-label="Close chat" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;
  }

  /**
   * Build input area HTML with speed toggle and send button
   */
  private buildInputHTML(speedModeEnabled: boolean): string {
    const placeholder = this.config.placeholder ?? 'Type a message...';
    const speedActiveClass = speedModeEnabled ? 'active' : '';

    return `
      <div class="pms-panel-input">
        <input
          type="text"
          class="pms-input"
          placeholder="${placeholder}"
          aria-label="Message input"
        />
        <button class="pms-speed-toggle ${speedActiveClass}" aria-label="Toggle speed mode" type="button" title="Speed mode: concise answers">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
        </button>
        <button class="pms-send-btn" aria-label="Send message" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
        <div class="pms-disclaimer">${t('panel.disclaimer')}</div>
      </div>
    `;
  }

  // ===========================================================================
  // ELEMENT CACHING
  // ===========================================================================

  /**
   * Cache DOM element references from a panel element
   */
  cacheElementReferences(panel: HTMLElement): PanelElements {
    return {
      panel,
      messagesContainer: panel.querySelector('.pms-panel-messages'),
      inputElement: panel.querySelector('.pms-input'),
      sendButton: panel.querySelector('.pms-send-btn'),
      speedToggleBtn: panel.querySelector('.pms-speed-toggle'),
    };
  }

  // ===========================================================================
  // MESSAGE RENDERING
  // ===========================================================================

  /**
   * Show welcome message with optional suggestions and blocks.
   * Filters sample questions based on authentication state (auto-detected from localStorage).
   */
  showWelcome(
    container: HTMLElement,
    onSuggestionClick: (suggestion: string) => void,
    onActionClick: (action: string) => void
  ): void {
    const greeting = this.config.greeting ?? 'Hello! How can I help you today?';
    // Filter sample questions based on auth state (auto-detects from localStorage if not explicit)
    const suggestions = filterSampleQuestions(
      this.config.sampleQuestions,
      this.config.isAuthenticated,
      this.config.authTokenKeys
    );
    const greetingBlocks = this.config.greetingBlocks;

    const welcome = createWelcomeMessage(
      greeting,
      suggestions.length > 0 ? suggestions : undefined,
      onSuggestionClick,
      greetingBlocks,
      onActionClick,
      this.config.vertical
    );
    container.appendChild(welcome);
  }

  /**
   * Render a chat message to the container
   */
  renderMessage(
    container: HTMLElement,
    message: ChatMessage,
    options: MessageRenderOptions
  ): void {
    renderMessage(message, container, options);
    this.scrollToBottom(container);
  }

  /**
   * Show an error message
   */
  showError(container: HTMLElement, message: string): void {
    const error = createErrorMessage(message);
    container.appendChild(error);
    this.scrollToBottom(container);
  }

  /**
   * Show a usage limit message (softer amber style, not error red)
   */
  showUsageLimitError(container: HTMLElement): void {
    const el = createUsageLimitMessage();
    container.appendChild(el);
    this.scrollToBottom(container);
  }

  // ===========================================================================
  // TYPING INDICATOR
  // ===========================================================================

  /**
   * Create and show typing indicator
   */
  showTypingIndicator(container: HTMLElement): HTMLElement {
    const indicator = createTypingIndicator('Thinking...');
    container.appendChild(indicator);
    this.scrollToBottom(container);
    return indicator;
  }

  /**
   * Remove typing indicator
   */
  hideTypingIndicator(indicator: HTMLElement | null): void {
    indicator?.remove();
  }

  // ===========================================================================
  // UI STATE
  // ===========================================================================

  /**
   * Set input enabled/disabled state.
   * Input stays always enabled so users can type while AI responds.
   * Only the send button visual changes (send ↔ stop icon).
   */
  setInputEnabled(_elements: PanelElements, _enabled: boolean): void {
    // No-op: input is always enabled now. Use setSendButtonMode() instead.
  }

  /**
   * Toggle the send button between send mode (arrow) and stop mode (square).
   */
  setSendButtonMode(elements: PanelElements, mode: 'send' | 'stop'): void {
    const btn = elements.sendButton;
    if (!btn) return;

    if (mode === 'stop') {
      btn.classList.add('pms-stop-mode');
      btn.setAttribute('aria-label', 'Stop generating');
      btn.title = 'Stop generating';
      btn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`;
    } else {
      btn.classList.remove('pms-stop-mode');
      btn.setAttribute('aria-label', 'Send message');
      btn.title = '';
      btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;
    }
  }

  /**
   * Update speed toggle button state
   */
  updateSpeedToggle(button: HTMLButtonElement | null, speedMode: boolean): void {
    if (button) {
      button.classList.toggle('active', speedMode);
      button.title = speedMode
        ? 'Speed mode ON: concise answers'
        : 'Speed mode: concise answers';
    }
  }

  /**
   * Scroll container to bottom
   */
  scrollToBottom(container: HTMLElement): void {
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Clear all messages from container
   */
  clearMessages(container: HTMLElement): void {
    container.innerHTML = '';
  }

  // ===========================================================================
  // IMMERSIVE MENU
  // ===========================================================================

  /**
   * Create immersive dropdown menu element
   */
  createImmersiveDropdown(): HTMLElement {
    const dropdown = document.createElement('div');
    dropdown.className = 'pms-immersive-dropdown';
    dropdown.innerHTML = `
      <button class="pms-immersive-dropdown-item" data-action="new-chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        New conversation
      </button>
      <div class="pms-immersive-dropdown-divider"></div>
      <button class="pms-immersive-dropdown-item" data-action="clear">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        Clear chat
      </button>
    `;
    return dropdown;
  }
}
